import { useState, useCallback, useEffect } from 'react';
import { TrainingApiService } from '../../../services/trainingApiService';
import {
  TrainedModelWithMetrics,
  UseTrainedModelsReturn
} from '../types';

/**
 * Hook for managing trained models in the sidebar
 * Provides state management and API operations for model list
 */
export const useTrainedModels = (sessionId: string): UseTrainedModelsReturn => {
  const [models, setModels] = useState<TrainedModelWithMetrics[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState<TrainedModelWithMetrics | null>(null);
  const [isDownloading, setIsDownloading] = useState<string | null>(null);

  /**
   * Fetch models from API
   */
  const refreshModels = useCallback(async () => {
    if (!sessionId) {
      setModels([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await TrainingApiService.listModelsDatabase(sessionId);

      if (response.success && response.data?.models) {
        // Map to TrainedModelWithMetrics (metrics lazy loaded later)
        const modelsWithMetrics: TrainedModelWithMetrics[] = response.data.models.map(model => ({
          ...model,
          metrics: undefined,
          trainingParams: undefined
        }));
        setModels(modelsWithMetrics);

        // Clear selection if selected model no longer exists
        if (selectedModel && !modelsWithMetrics.find(m => m.filename === selectedModel.filename)) {
          setSelectedModel(null);
        }
      } else {
        setModels([]);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load models';
      setError(errorMessage);
      console.error('Error loading models:', err);
    } finally {
      setLoading(false);
    }
  }, [sessionId, selectedModel]);

  /**
   * Auto-refresh when session changes
   */
  useEffect(() => {
    if (sessionId) {
      refreshModels();
    } else {
      setModels([]);
      setSelectedModel(null);
    }
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Select a model by filename
   */
  const selectModel = useCallback((filename: string) => {
    const model = models.find(m => m.filename === filename);
    setSelectedModel(model || null);
  }, [models]);

  /**
   * Download model file
   */
  const downloadModel = useCallback(async (filename: string) => {
    if (!sessionId) return;

    setIsDownloading(filename);
    try {
      await TrainingApiService.downloadModelH5(sessionId, filename);
    } catch (err) {
      console.error('Error downloading model:', err);
      setError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setIsDownloading(null);
    }
  }, [sessionId]);

  return {
    models,
    loading,
    error,
    selectedModel,
    refreshModels,
    selectModel,
    downloadModel,
    isDownloading
  };
};
